import { useDate } from 'root/src/client/v2/common/hooks/useDate';
import { useFormContext } from 'react-hook-form';
import { memo } from 'react';
import { useCreateGiveawayFormContext, } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/contexts/createGiveawayForm.context';
const enOrdinalRules = new Intl.PluralRules('en-US', { type: 'ordinal' });
const suffixes = new Map([
    ['one', 'st'],
    ['two', 'nd'],
    ['few', 'rd'],
    ['other', 'th'],
]);
export const SchedulerPreview = memo(() => {
    return (<table className="border-separate border-spacing-2 self-center">
			<caption>
				<p className="uppercase font-medium text-light-5 my-2">Winner selection date</p>
			</caption>
			<tbody>
				<NextSelectionRow />
				<NSelectionRow />
				<LastSelectionRow />
			</tbody>
		</table>);
});
class SchedulerPreviewHelper {
    static getNextDate(params) {
        var _a, _b;
        const startDate = new Date((_b = (_a = params.giveaway) === null || _a === void 0 ? void 0 : _a.endDate) !== null && _b !== void 0 ? _b : params.date);
        if (params.scheduler) {
            const hasNext = params.scheduler.cycle + 1 <= Number(params.repeatTimes);
            if (!hasNext) {
                return null;
            }
        }
        return new Date(startDate.getTime() + params.durationTime);
    }
    static getNDate(params) {
        var _a, _b;
        const startDate = new Date((_b = (_a = params.giveaway) === null || _a === void 0 ? void 0 : _a.endDate) !== null && _b !== void 0 ? _b : params.date);
        if (!params.repeatTimes || !params.isRepeatingEnabled) {
            return null;
        }
        if (params.scheduler) {
            const isCycleFinished = params.scheduler.cycle + 2 > Number(params.repeatTimes);
            if (isCycleFinished) {
                return null;
            }
        }
        return new Date(startDate.getTime() + params.durationTime * 2);
    }
    static getLastDate(params) {
        var _a, _b;
        const startDate = new Date((_b = (_a = params.giveaway) === null || _a === void 0 ? void 0 : _a.endDate) !== null && _b !== void 0 ? _b : params.date);
        if (!params.repeatTimes || !params.isRepeatingEnabled) {
            return null;
        }
        if (params.scheduler) {
            const isCycleFinished = params.scheduler.cycle + 1 > Number(params.repeatTimes);
            if (isCycleFinished) {
                return null;
            }
            return new Date(startDate.getTime() + params.durationTime * (params.repeatTimes - params.scheduler.cycle));
        }
        return new Date(startDate.getTime() + params.durationTime * (params.repeatTimes + 1));
    }
}
const useSchedulerPreviewContext = () => {
    const date = useDate();
    const form = useFormContext();
    const { giveaway, scheduler } = useCreateGiveawayFormContext();
    const durationTime = form.watch('durationTime');
    const repeatTimes = form.watch('repeatTimes');
    const isRepeatingEnabled = form.watch('isRepeatingEnabled');
    return {
        date,
        giveaway,
        scheduler,
        durationTime,
        repeatTimes,
        isRepeatingEnabled,
    };
};
const CurrentSelection = memo(() => {
    const { giveaway } = useSchedulerPreviewContext();
    if (!giveaway || !giveaway.endDate) {
        return null;
    }
    return (<>
			<td className="uppercase text-xs text-light-4 text-right">
				Current giveaway:
			</td>
			<td>
				<DateView date={new Date(giveaway.endDate)}/>
			</td>
		</>);
});
const LastSelectionRow = memo(() => {
    const schedulerPreviewContext = useSchedulerPreviewContext();
    const last = SchedulerPreviewHelper.getLastDate(schedulerPreviewContext);
    const n = SchedulerPreviewHelper.getNDate(schedulerPreviewContext);
    if (!last) {
        return null;
    }
    if (last.toISOString() === (n === null || n === void 0 ? void 0 : n.toISOString())) {
        return null;
    }
    return (<tr>
			<td className="uppercase text-xs text-light-4 text-right">
				Last giveaway:
			</td>
			<td>
				<DateView date={last}/>
			</td>
		</tr>);
});
const NSelectionRow = memo(() => {
    var _a, _b;
    const schedulerPreviewContext = useSchedulerPreviewContext();
    const n = SchedulerPreviewHelper.getNDate(schedulerPreviewContext);
    if (!n) {
        return null;
    }
    return (<tr>
			<td className="uppercase text-xs text-light-4 text-right">
				{formatOrdinals(((_b = (_a = schedulerPreviewContext.scheduler) === null || _a === void 0 ? void 0 : _a.cycle) !== null && _b !== void 0 ? _b : 0) + 2)} giveaway:
			</td>
			<td>
				<DateView date={n}/>
			</td>
		</tr>);
});
const formatOrdinals = (n) => {
    const rule = enOrdinalRules.select(n);
    const suffix = suffixes.get(rule);
    return `${n}${suffix}`;
};
const NextSelectionRow = memo(() => {
    const schedulerPreviewContext = useSchedulerPreviewContext();
    const next = SchedulerPreviewHelper.getNextDate(schedulerPreviewContext);
    if (!next) {
        return <CurrentSelection />;
    }
    return (<tr>
			<td className="uppercase text-xs text-light-4 text-right">
				Next giveaway:
			</td>
			<td>
				<DateView date={next}/>
			</td>
		</tr>);
});
const DateView = memo((props) => {
    const { date } = props;
    return (<div className="flex justify-center items-center gap-2">
			{date.toLocaleDateString(undefined, {
            month: 'long',
            year: 'numeric',
            day: 'numeric',
        })}
			<DateViewDivider />
			{date.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
        })}
		</div>);
});
const DateViewDivider = memo(() => {
    return <span className="text-xs text-light-4">@</span>;
});
